import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../config/api";
import EditModal from "../components/EditModal";
import "./Coders.css";
import "../styles/buttons.css";
import { HiPencilSquare } from "react-icons/hi2";
import {
  useCoderFilters,
  type Coder,
  type CoderFilters,
} from "../hooks/useCoderFilters";
import {
  WITHOUT_ENTERPRISE,
  appendEnterpriseFilter,
  enterpriseFilterLabel,
  enterpriseFilterOptions,
} from "../utils/enterpriseFilter";
import Select from "react-select";
import { toast } from "react-toastify";
import { syncSummary } from "../utils/syncSummary";
import { syncEntity } from "../utils/syncEntity";
import { errorMessage } from "../utils/errors";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import { formatLanguage } from "../utils/language";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";

/** The filters the API applies; the sort is not among them. */
type CoderQuery = Pick<
  CoderFilters,
  "search" | "enterprise" | "country" | "language"
>;

const Coders: React.FC = () => {
  const [coders, setCoders] = useState<Coder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCoder, setSelectedCoder] = useState<Coder | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // `?withoutEnterprise=true` opens the page on the coders belonging to no enterprise —
  // how the dashboard's Codeurs card links to them. Read once, as the initial filter
  // state, so the filter can then be changed without the URL pulling it back.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo<Partial<CoderFilters> | undefined>(
    () =>
      searchParams.get("withoutEnterprise") === "true"
        ? { enterprise: WITHOUT_ENTERPRISE }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Use the filtering hook
  const {
    filters,
    filteredCoders,
    updateFilter,
    resetFilters,
    hasActiveFilters,
  } = useCoderFilters(coders, initialFilters);

  // Dropdown choices come from the API so they cover every coder, not only the
  // ones on the current page.
  const [filterOptions, setFilterOptions] = useState<{
    enterprises: string[];
    countries: string[];
    languages: string[];
  }>({ enterprises: [], countries: [], languages: [] });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/coders/filter-options");
        setFilterOptions(response.data);
      } catch (error) {
        console.error("Error loading coder filter options:", error);
      }
    };
    loadFilterOptions();
  }, []);

  // Debounced copy of the search box: it is sent to the API, so it must not
  // fire a request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Only the very first load shows the page-level spinner. Later fetches must
  // reload the rows in place: the spinner replaces the whole page, which would
  // unmount the search box and take the focus away mid-typing.
  const hasLoadedOnce = useRef(false);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<CoderQuery>(
    () => ({
      search: debouncedSearch,
      enterprise: filters.enterprise,
      country: filters.country,
      language: filters.language,
    }),
    [debouncedSearch, filters.enterprise, filters.country, filters.language],
  );

  useEffect(() => {
    fetchCoders(currentPage, pageSize, activeQuery, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `query` is matched by the API across every coder, not only the rows of the
   * current page.
   *
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   * Used after a sync, where the page is already on screen.
   */
  const fetchCoders = async (
    page: number,
    limit: number,
    query: CoderQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query.search) params.append("search", query.search);
      appendEnterpriseFilter(params, query.enterprise);
      if (query.country) params.append("country", query.country);
      if (query.language) params.append("language", query.language);

      const response = await api.get(`/api/coders?${params.toString()}`);
      const { data, pagination } = response.data;

      setCoders(data);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
      setCurrentPage(pagination.page);
    } catch (error) {
      if (silent) {
        toast.error("Erreur lors du rechargement des codeurs");
      } else {
        setError("Erreur lors du chargement des codeurs");
      }
      console.error("Error fetching coders:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const syncCoders = async () => {
    try {
      setSyncing(true);
      const data = await syncEntity("coders");

      if (data.success) {
        // Refresh the rows in place, keeping the active filters
        await fetchCoders(currentPage, pageSize, activeQuery, true);
        toast.success(syncSummary(data));
      } else {
        toast.error("Échec de la synchronisation : " + (data.message ?? ""));
      }
    } catch (error) {
      toast.error("Échec de la synchronisation : " + errorMessage(error));
      console.error("Sync error:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleEditCoder = (coder: Coder) => {
    setSelectedCoder(coder);
    setEditModalOpen(true);
  };

  const handleSaveCoder = (updatedCoder: Coder) => {
    setCoders((prev) =>
      prev.map((coder) =>
        coder.id === updatedCoder.id ? updatedCoder : coder,
      ),
    );
    setEditModalOpen(false);
    setSelectedCoder(null);
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleFilterChange = (key: keyof CoderFilters, value: string) => {
    updateFilter(key, value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    resetFilters();
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des codeurs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchCoders(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="coders-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Codeurs</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncing ? "syncing" : ""}`}
                onClick={syncCoders}
                disabled={syncing}
              >
                {syncing ? "Synchronisation..." : "Synchroniser Codeurs"}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Recherche</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Nom ou email..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Entreprise</label>
              <Select
                value={
                  filters.enterprise
                    ? {
                        value: filters.enterprise,
                        label: enterpriseFilterLabel(filters.enterprise),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("enterprise", option ? option.value : "")
                }
                // "Sans entreprise" sits alongside the names so the state a dashboard
                // link arrives in is one the dropdown can also express and clear.
                options={enterpriseFilterOptions(
                  filterOptions.enterprises,
                  "Toutes les entreprises",
                )}
                styles={darkSelectStyles}
                placeholder="Toutes les entreprises"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Pays</label>
              <Select
                value={
                  filters.country
                    ? { value: filters.country, label: filters.country }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("country", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les pays" },
                  ...filterOptions.countries.map((country) => ({
                    value: country,
                    label: country,
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les pays"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Langue</label>
              <Select
                value={
                  filters.language
                    ? {
                        value: filters.language,
                        label: formatLanguage(filters.language),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("language", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Toutes les langues" },
                  ...filterOptions.languages.map((language) => ({
                    value: language,
                    label: formatLanguage(language),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Toutes les langues"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="coders-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Entreprise</th>
                  <th>Pays</th>
                  <th>Langue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoders.map((coder) => (
                  <tr key={coder.id}>
                    <td>{coder.aia_coder_id}</td>
                    <td>
                      {coder.first_name} {coder.last_name}
                      {coder.enterprise?.sendContracts === false && (
                        <span
                          className="contract-blocked-badge"
                          title="L'envoi de contrats est désactivé pour cette entreprise"
                        >
                          Contrats bloqués
                        </span>
                      )}
                    </td>
                    <td>{coder.email}</td>
                    <td>{coder.enterprise?.nom || EMPTY_VALUE}</td>
                    <td>{coder.enterprise?.country || EMPTY_VALUE}</td>
                    <td>{formatLanguage(coder.enterprise?.language)}</td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditCoder(coder)}
                        title="Modifier le codeur"
                      >
                        <HiPencilSquare />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Page {currentPage} sur {totalPages} ({totalItems} codeurs au
                total)
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      </div>

      {editModalOpen && selectedCoder && (
        <EditModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedCoder(null);
          }}
          onSave={handleSaveCoder}
          type="coder"
          data={selectedCoder}
        />
      )}
    </div>
  );
};

export default Coders;
